"use client";

import {
  MutationCache,
  notifyManager,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import {
  handleGlobalMutationError,
  handleGlobalQueryError,
} from "@/lib/queries/query-error-handling";
import { type AnyRouter, useRouter } from "@tanstack/react-router";
import { useAuthStore } from "@/lib/store/auth";

type Viewer = {
  readonly userId: string | null;
  readonly clientId: string | null;
};

// What a query returns depends on who is asking: the signed-in user and the
// client they act for, which the route names. Most query keys carry neither,
// and the cache outlives both, so when either changes away from a known value
// every cached answer belongs to someone else. The auth store notifies
// synchronously on `set`, and the router before it loads the next location,
// both before React renders the new viewer, so this:
//   - cancels in-flight reads (cancellation takes effect synchronously) without
//     reverting, which would restore the previous viewer's data;
//   - resets every query, which notifies the components still mounted on it,
//     so none keeps rendering the previous viewer's result;
//   - refetches the active queries only on the next task, once React has
//     rendered the new viewer and each mounted query holds the query function
//     that reads as them, and only if no later change has superseded this one.
// After a sign-out there is nobody to read for, so nothing refetches.
export function resetCacheOnViewerChange(
  queryClient: QueryClient,
  router: Pick<AnyRouter, "getMatchedRoutes" | "state" | "subscribe">,
) {
  const clientAt = (pathname: string) =>
    router.getMatchedRoutes(pathname).routeParams["clientId"] ?? null;
  let viewer: Viewer = {
    userId: useAuthStore.getState().user?.id ?? null,
    clientId: clientAt(router.state.location.pathname),
  };
  let generation = 0;
  const onViewerChange = (next: Viewer) => {
    const left =
      (viewer.userId !== null && next.userId !== viewer.userId) ||
      (viewer.clientId !== null && next.clientId !== viewer.clientId);
    viewer = next;
    if (!left) return;
    generation += 1;
    const change = generation;
    void queryClient.cancelQueries(undefined, { revert: false, silent: true });
    notifyManager.batch(() => {
      for (const query of queryClient.getQueryCache().getAll()) {
        query.reset();
      }
    });
    if (next.userId === null) return;
    setTimeout(() => {
      if (change !== generation) return;
      void queryClient.refetchQueries(
        { type: "active" },
        { cancelRefetch: false },
      );
    }, 0);
  };
  const unsubscribeAuth = useAuthStore.subscribe((state) =>
    onViewerChange({ ...viewer, userId: state.user?.id ?? null }),
  );
  const unsubscribeRouter = router.subscribe(
    "onBeforeNavigate",
    ({ toLocation }) =>
      onViewerChange({ ...viewer, clientId: clientAt(toLocation.pathname) }),
  );
  return () => {
    unsubscribeAuth();
    unsubscribeRouter();
  };
}

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => {
    return new QueryClient({
      // Every query/mutation failure reaches Sentry by default (see
      // query-error-handling.ts). Queries also get a user-visible toast;
      // mutations don't — the caller propagates the rejection and owns the
      // user-facing signal. Opt a specific call site out of capture with
      // `meta: { suppressGlobalError: true }`.
      queryCache: new QueryCache({
        onError: handleGlobalQueryError,
      }),
      mutationCache: new MutationCache({
        onError: handleGlobalMutationError,
      }),
      defaultOptions: {
        queries: {
          staleTime: 60 * 1000, // 1 minute
          retry: 1,
          refetchOnWindowFocus: false,
          gcTime: 5 * 60 * 1000, // 5 minutes
        },
        mutations: {
          // No default options needed for mutations
        },
      },
      // Note: Auth error handling (401, 403+INVALID_TOKEN) is done in axios-client.ts response interceptor
      // No need for duplicate error handlers here
    });
  });

  const router = useRouter();
  useEffect(
    () => resetCacheOnViewerChange(queryClient, router),
    [queryClient, router],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
